import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';

import { profileApi } from '@internal/api/profile';
import { projectsApi } from '@internal/api/projects';
import { getProjectActionErrorMessage } from '@internal/api/projects/projects';
import { tagsApi } from '@internal/api/tags';
import BackLink from '@internal/components/app/components/back-link';
import Logo from '@internal/components/app/components/side-nav/lemon-logo.svg';
import StatusPill from '@internal/components/app/components/status-pill';
import {
  canApplyToProjects,
  isPartnerAccount,
} from '@internal/components/app/constants';
import {
  isRoleVerified,
  useCandidateRoles,
} from '@internal/components/app/roles';
import {
  BannerModel,
  buildClosedProjectBanner,
  buildOfferBanner,
} from '@internal/components/application-view/application-progress';
import ApplicationStateBanner from '@internal/components/application-view/application-state-banner';
import { getApplicationTargetHref } from '@internal/components/application-view/use-application-model';
import { useApiContext } from '@internal/components/common/api-context';
import Loader from '@internal/components/common/loader';
import Markdown from '@internal/components/common/markdown';
import { adjustUrl } from '@internal/components/developers/utils';
import {
  basicInfoRows,
  hasTextContent,
} from '@internal/components/project-view/project-basic-info';
import {
  getApplicationHref,
  getApplicationState,
  hasSubmittedApplication,
  isAppliedState,
  isClosedUnsubmittedOffer,
} from '@internal/components/projects/helper/application-state';
import { resolveProjectRole } from '@internal/components/projects/helper/project-role';
import {
  canSelfApplyTo,
  getProjectBackTarget,
  hasValidHiringPosition,
  isProjectClosed,
  isProjectOpen,
  PROJECT_FROM_QUERY_KEY,
  projectOfferStatusMap,
} from '@internal/components/projects/helper/projects.helpers';
import Routes from '@internal/config/routes';
import {
  ApplicationState,
  ProjectOfferStatus,
  ProjectSkill,
} from '@internal/types/projects';
import { Tag } from '@internal/types/tags';
import {
  ApplicationSource,
  MEvents,
  projectApplicationMEvent,
} from '@internal/utils/mixpanel';
import { toPlural } from '@internal/utils/plural';
import { REDIRECT_QUERY_PARAM } from '@internal/utils/safe-redirect';

import ApplyCard, { ApplyCardKind } from './apply-card';
import ApplyForm, { ApplyDrawerMode } from './apply-form';
import DeclineOfferDialog from './decline-offer-dialog';
import ProjectApplicationBanner from './project-application-banner';
import {
  ProjectProgressNote,
  SelectionProcessCard,
} from './project-selection-process';
import {
  BasicInfoColumn,
  BasicInfoGrid,
  BasicInfoLabel,
  BasicInfoRow,
  BlackActionButton,
  BodyText,
  CardTitle,
  ClientGoalLabel,
  ContactButton,
  DirectHireBanner,
  DirectHireBody,
  DirectHireTitle,
  HeaderArea,
  HeaderChip,
  HeaderRow,
  MainColumn,
  MatcherLabel,
  MatcherName,
  MetaRow,
  PageColumn,
  PageGrid,
  PageRoot,
  PageStatusBanner,
  PageTitle,
  PillList,
  ProjectId,
  PublicLogoBar,
  SectionCard,
  SideColumn,
  SignInHint,
  SignInLink,
  SkillPill,
  SkillRow,
  SkillRowLabel,
  SkillRows,
  Subtitle,
  SummaryLink,
} from './project-view.styled';
import { useApplyDrawerWidth } from './use-apply-drawer-width';

export const APPLY_QUERY_KEY = 'apply';

const APPLICATION_RECEIVED_MESSAGE =
  'Application received. Complete your pending steps and wait for the team review.';

const OFFER_DECLINED_MESSAGE =
  'Offer declined. Your profile and role progress stay saved.';

const DECLINE_FAILED_MESSAGE =
  'The offer could not be declined. Please try again.';

const relevantFirst =
  <T,>(isRelevant: (item: T) => boolean) =>
  (a: T, b: T): number =>
    Number(isRelevant(b)) - Number(isRelevant(a));

const skillLabel = (skill: ProjectSkill): string =>
  skill.years
    ? `${skill.name} - ${skill.years} ${toPlural({
        word: 'Year',
        isPlular: skill.years > 1,
        ending: 's',
      })}`
    : skill.name;

const SkillsRow: React.FC<{
  label: string;
  items: Array<{ key: string; label: string }>;
}> = ({ label, items }) => (
  <SkillRow>
    <SkillRowLabel>{label}</SkillRowLabel>
    <PillList>
      {items.map(item => (
        <SkillPill key={item.key}>{item.label}</SkillPill>
      ))}
    </PillList>
  </SkillRow>
);

const TextSection: React.FC<{ title: string; text?: string | null }> = ({
  title,
  text,
}) => {
  if (!hasTextContent(text)) return null;
  return (
    <SectionCard>
      <CardTitle>{title}</CardTitle>
      <BodyText as="div">
        <Markdown value={text} />
      </BodyText>
    </SectionCard>
  );
};

const ProjectViewPage: React.FC<{ dealId: string; developerId?: string }> = ({
  dealId,
  developerId,
}) => {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useApiContext();
  const candidateRoles = useCandidateRoles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(ApplyDrawerMode.Apply);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [mainColumn, setMainColumn] = useState<HTMLElement | null>(null);
  const drawerWidth = useApplyDrawerWidth(mainColumn);
  const autoOpened = useRef(false);

  const { project, projectIsLoading } = projectsApi.getProject.useQuery({
    dealId,
    developerId,
    role: profile?.role,
  });
  const { declineProject, declineProjectLoading } =
    projectsApi.declineProject.useMutation();

  const { skills } = profileApi.getSkills.useQuery(profile?.apiKey, {
    enabled: Boolean(profile?.apiKey && profile.role !== 'partner manager'),
  });
  const { contactProjectTags } = tagsApi.getContactProjectTags.useQuery(
    { contactKey: profile?.apiKey as string },
    { enabled: Boolean(profile?.apiKey) }
  );
  const { hubspotProjectTags } = tagsApi.getHubspotProjectTags.useQuery({
    hubspotProjectId: dealId,
  });

  const tagsList = useMemo(
    () =>
      Object.values(contactProjectTags.projectTags).reduce<Tag[]>(
        (acc, tags) => [
          ...acc,
          ...tags.filter(tag => !acc.find(accTag => accTag.name === tag.name)),
        ],
        []
      ),
    [contactProjectTags]
  );

  const isPartnerManager = profile?.role === 'partner manager';
  const isCandidateView =
    profile?.role === 'candidate' || profile?.role === 'developer';
  const state = project ? getApplicationState(project) : null;
  const submitted = Boolean(project && hasSubmittedApplication(project));
  const isOpen = project ? isProjectOpen(project) : false;
  const isClosed = project ? isProjectClosed(project) : true;

  const hasOffer = state === ApplicationState.Offered;
  const applicable = hasOffer
    ? !isClosed && Boolean(project && hasValidHiringPosition(project))
    : Boolean(project && canSelfApplyTo(project));
  const offerEnded =
    !submitted &&
    (state === ApplicationState.OfferDeclined ||
      state === ApplicationState.NotSelected);
  const isPartnerDeveloper = profile?.role === 'partner developer';
  // Partner developers cannot self-apply; they only answer manual Matching offers.
  const eligible =
    canApplyToProjects(profile) || (isPartnerDeveloper && hasOffer);

  const canApply = isPartnerManager
    ? hasOffer && !submitted && applicable
    : Boolean(profile) && eligible && !submitted && applicable && !offerEnded;

  const openDrawer = (mode: ApplyDrawerMode) => {
    setDrawerMode(mode);
    setDrawerOpen(true);
  };

  const showApplication = isCandidateView && isAppliedState(state);
  useEffect(() => {
    if (!showApplication || !project) return;
    router
      .replace(
        getApplicationHref(project, router.query[PROJECT_FROM_QUERY_KEY])
      )
      .catch(() => {});
  }, [showApplication, project, router]);

  const applyQuery = router.query[APPLY_QUERY_KEY];
  useEffect(() => {
    if (!project || autoOpened.current || applyQuery !== '1') return;
    autoOpened.current = true;
    if (canApply) openDrawer(ApplyDrawerMode.Apply);
    const { [APPLY_QUERY_KEY]: _apply, ...rest } = router.query;
    router
      .replace({ pathname: router.pathname, query: rest }, undefined, {
        shallow: true,
      })
      .catch(() => {});
  }, [project, applyQuery, canApply, router]);

  if (!project) {
    return projectIsLoading ? <Loader open /> : null;
  }
  if (showApplication) return <Loader open />;

  const canViewSummary = submitted && profile?.role !== 'partner developer';
  const partnerClosedOffer =
    isPartnerAccount(profile) && isClosedUnsubmittedOffer(project);
  const showCandidateSelection = !profile || isCandidateView;
  const showApplyDrawer = Boolean(profile) && (canApply || canViewSummary);

  const applyPaused =
    isCandidateView && !eligible && !submitted && !offerEnded && applicable;

  let applyCard: ApplyCardKind | null = null;
  if (!profile) {
    applyCard = ApplyCardKind.Public;
  } else if (canApply || applyPaused) {
    applyCard = hasOffer ? ApplyCardKind.Offer : ApplyCardKind.SelfApply;
  } else if (isCandidateView && !submitted && !offerEnded && !applicable) {
    applyCard = ApplyCardKind.Closed;
  }
  const showApplyCard = applyCard !== null;
  const showClosedChip =
    partnerClosedOffer ||
    (isCandidateView && (isClosed || (!state && !canSelfApplyTo(project))));

  const showContactBlock =
    (profile?.role === 'developer' || profile?.role === 'partner manager') &&
    isOpen;
  const showProjectSlackChannel = Boolean(submitted && project.slackURL);
  const showMatcherBlock = showContactBlock || showProjectSlackChannel;
  const matcherSlackUrl =
    submitted && project.slackURL
      ? project.slackURL
      : profile?.chatLink ?? null;
  const showSideColumn = showApplyCard || showMatcherBlock;

  const handleDeclineProject = (declineComment: string) => {
    if (!profile) return;
    declineProject(
      {
        contactId: project.contactId,
        hubspotProjectId: project.id,
        dealId: project.dealId,
        declineComment,
        apiKey: profile.apiKey,
      },
      {
        onSuccess: () => {
          setDeclineModalOpen(false);
          projectApplicationMEvent(
            MEvents.ProjectOfferDeclined,
            project.dealId,
            ApplicationSource.Offer
          );
          if (isCandidateView) {
            enqueueSnackbar({
              variant: 'success',
              message: OFFER_DECLINED_MESSAGE,
            });
          }
        },
        onError: error => {
          enqueueSnackbar({
            variant: 'error',
            message: getProjectActionErrorMessage(
              error,
              DECLINE_FAILED_MESSAGE
            ),
          });
        },
      }
    );
  };

  const handleSubmitted = () => {
    if (!isCandidateView) {
      setDrawerMode(ApplyDrawerMode.Summary);
      return;
    }
    enqueueSnackbar({
      variant: 'success',
      message: APPLICATION_RECEIVED_MESSAGE,
    });

    router
      .push(getApplicationHref(project, router.query[PROJECT_FROM_QUERY_KEY]))
      .catch(() => {});
  };

  const { role: projectRole, label: subtitle } = resolveProjectRole(
    project,
    candidateRoles
  );
  const userSkills = new Set((skills ?? []).map(skill => skill.name));
  const bySkillRelevance = relevantFirst((skill: ProjectSkill) =>
    userSkills.has(skill.name)
  );
  const mustHave = [...(project.projectMustHaveSkills ?? [])]
    .sort(bySkillRelevance)
    .map(skill => ({ key: String(skill.id), label: skillLabel(skill) }));
  const niceToHave = [...(project.projectNiceToHaveSkills ?? [])]
    .sort(bySkillRelevance)
    .map(skill => ({ key: String(skill.id), label: skillLabel(skill) }));
  const softSkills = (project.projectSoftSkills ?? []).map(skill => ({
    key: skill,
    label: skill,
  }));
  const relevantTagIds = new Set(tagsList.map(tag => tag.id));
  const experienceTags = [...hubspotProjectTags]
    .sort(relevantFirst((tag: Tag) => relevantTagIds.has(tag.id)))
    .map(tag => ({ key: String(tag.id), label: tag.name }));
  const infoRows = basicInfoRows(project, { showRate: !isPartnerDeveloper });
  const infoSplit = Math.ceil(infoRows.length / 2);
  const infoColumns = [
    infoRows.slice(0, infoSplit),
    infoRows.slice(infoSplit),
  ].filter(column => column.length > 0);
  const showSkillsCard =
    mustHave.length > 0 ||
    niceToHave.length > 0 ||
    experienceTags.length > 0 ||
    softSkills.length > 0;
  const clientGoal =
    hasTextContent(project.projectGoal) && project.projectGoal !== 'Other'
      ? project.projectGoal
      : null;

  const backTarget = getProjectBackTarget(
    router.query[PROJECT_FROM_QUERY_KEY],
    profile?.role
  );
  const goBack = () => {
    router.push(backTarget.href).catch(() => {});
  };
  const goToProjects = () => {
    router.push(Routes.OpenProjects).catch(() => {});
  };

  const goToLogin = () => {
    const loginUrl = new URL(
      `${process.env.APP_LEMON_URL as string}/auth/login`
    );
    loginUrl.searchParams.append('redirect', window.location.href);
    window.location.href = loginUrl.toString();
  };

  const goToExploreSignup = () => {
    router
      .push({
        pathname: Routes.EscapeTheMatrix,
        query: { [REDIRECT_QUERY_PARAM]: Routes.OpenProjects },
      })
      .catch(() => {});
  };

  const goToSignup = () => {
    router
      .push({
        pathname: Routes.EscapeTheMatrix,
        query: {
          [REDIRECT_QUERY_PARAM]: `${Routes.ViewProject}/${dealId}?${APPLY_QUERY_KEY}=1`,
        },
      })
      .catch(() => {});
  };

  const showApplicationBanner =
    isCandidateView && submitted && Boolean(profile);
  const unavailableOffer = hasOffer && !applicable;
  const showNewOfferChip = isCandidateView && hasOffer && !unavailableOffer;
  let offerBanner: BannerModel | null = null;
  if (partnerClosedOffer || unavailableOffer) {
    offerBanner = buildClosedProjectBanner();
  } else if (isCandidateView && !showNewOfferChip) {
    offerBanner = buildOfferBanner(state);
  }
  const legacyOfferStatus =
    project.offerStatus === ProjectOfferStatus.NewOffer && submitted
      ? ProjectOfferStatus.ApplicationSubmitted
      : project.offerStatus;
  const showLegacyBanner =
    !showApplicationBanner &&
    !offerBanner &&
    !showNewOfferChip &&
    Boolean(legacyOfferStatus) &&
    (!isCandidateView || !isClosed);
  const legacyMeta =
    showLegacyBanner && legacyOfferStatus
      ? projectOfferStatusMap[legacyOfferStatus]
      : null;

  return (
    <PageRoot $public={!profile}>
      {!profile && (
        <PublicLogoBar>
          <Image src={Logo as string} alt="Lemon.io" width={112} height={32} />
        </PublicLogoBar>
      )}

      <PageColumn>
        <PageGrid $withSide={showSideColumn}>
          <HeaderArea>
            {profile && (
              <BackLink
                label={backTarget.label}
                onClick={goBack}
                dataTestId="project-back"
              />
            )}

            <HeaderRow>
              <PageTitle>{project.projectName}</PageTitle>
              {profile && showClosedChip && (
                <HeaderChip>
                  <StatusPill variant="closed">Project closed</StatusPill>
                </HeaderChip>
              )}
              {profile && showNewOfferChip && (
                <HeaderChip data-testid="project-new-offer-chip">
                  <StatusPill variant="new">New offer</StatusPill>
                </HeaderChip>
              )}
            </HeaderRow>
            <MetaRow>
              <ProjectId>#{project.dealId}</ProjectId>
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </MetaRow>

            {showApplicationBanner && profile && (
              <ProjectApplicationBanner
                project={project}
                profile={profile}
                onSummary={() => openDrawer(ApplyDrawerMode.Summary)}
              />
            )}

            {!showApplicationBanner && offerBanner && (
              <ApplicationStateBanner
                banner={offerBanner}
                showSummary={false}
                onSummary={() => openDrawer(ApplyDrawerMode.Summary)}
                onAction={action =>
                  router
                    .push(getApplicationTargetHref(action.target, project))
                    .catch(() => {})
                }
              />
            )}

            {legacyMeta && (
              <PageStatusBanner
                severity="info"
                title={
                  project.partnerDeveloper
                    ? legacyMeta.renderTitle(
                        project.partnerDeveloper.firstName,
                        project.partnerDeveloper.lastName
                      )
                    : legacyMeta.title
                }
                action={
                  canViewSummary && (
                    <>
                      <SummaryLink
                        type="button"
                        onClick={() => openDrawer(ApplyDrawerMode.Summary)}
                        data-testid="project-view-summary"
                      >
                        View summary
                      </SummaryLink>
                      {!isPartnerAccount(profile) && (
                        <BlackActionButton
                          type="button"
                          onClick={goToProjects}
                          data-testid="project-explore-others"
                        >
                          Explore other projects
                        </BlackActionButton>
                      )}
                    </>
                  )
                }
              >
                <Markdown
                  value={
                    project.declineFeedback?.trim()
                      ? project.declineFeedback
                      : legacyMeta.infoAlertText
                  }
                />
              </PageStatusBanner>
            )}
          </HeaderArea>

          <MainColumn ref={setMainColumn}>
            {(infoRows.length > 0 ||
              project.projectMustBeAvailableForDirectHire) && (
              <SectionCard>
                <CardTitle>Basic info</CardTitle>
                {infoRows.length > 0 && (
                  <BasicInfoGrid>
                    {infoColumns.map(column => (
                      <BasicInfoColumn key={column[0].label}>
                        {column.map(row => (
                          <BasicInfoRow key={row.label}>
                            <BasicInfoLabel>{row.label}</BasicInfoLabel>
                            {row.value}
                          </BasicInfoRow>
                        ))}
                      </BasicInfoColumn>
                    ))}
                  </BasicInfoGrid>
                )}

                {project.projectMustBeAvailableForDirectHire && (
                  <DirectHireBanner>
                    <DirectHireTitle>Direct client offer</DirectHireTitle>
                    <DirectHireBody>
                      The client is looking for in-house hire
                    </DirectHireBody>
                  </DirectHireBanner>
                )}
              </SectionCard>
            )}

            {showSkillsCard && (
              <SectionCard>
                <CardTitle>Skills</CardTitle>
                <SkillRows>
                  {mustHave.length > 0 && (
                    <SkillsRow label="Must-have:" items={mustHave} />
                  )}
                  {niceToHave.length > 0 && (
                    <SkillsRow label="Nice to have:" items={niceToHave} />
                  )}
                  {experienceTags.length > 0 && (
                    <SkillsRow
                      label="Nice to have experience with/in:"
                      items={experienceTags}
                    />
                  )}
                  {softSkills.length > 0 && (
                    <SkillsRow
                      label="Required soft skills:"
                      items={softSkills}
                    />
                  )}
                </SkillRows>
              </SectionCard>
            )}

            <TextSection
              title="Note from a customer"
              text={project.projectSoftSkillsDetailed}
            />

            {(clientGoal || hasTextContent(project.projectDescription)) && (
              <SectionCard>
                <CardTitle>Project description</CardTitle>
                {clientGoal && (
                  <BodyText as="div">
                    <ClientGoalLabel>Client&apos;s goal:</ClientGoalLabel>{' '}
                    <Markdown value={clientGoal} />
                  </BodyText>
                )}
                {hasTextContent(project.projectDescription) && (
                  <BodyText as="div">
                    <Markdown value={project.projectDescription} />
                  </BodyText>
                )}
              </SectionCard>
            )}

            <TextSection
              title="Project tech stack"
              text={project.projectProjectTechStack}
            />
            <TextSection
              title={
                showCandidateSelection
                  ? 'Client selection process'
                  : 'Selection process'
              }
              text={project.projectSelectionProcess}
            />
            <TextSection
              title="Team and communication"
              text={project.projectTeamCommunication}
            />
            <TextSection title="Team size" text={project.projectDevTeamSize} />

            {showCandidateSelection && (
              <>
                <SelectionProcessCard roleLabel={subtitle} />
                {(!profile || eligible) && (
                  <ProjectProgressNote
                    roleVerified={Boolean(
                      profile && projectRole && isRoleVerified(projectRole)
                    )}
                  />
                )}
              </>
            )}
          </MainColumn>

          {showSideColumn && (
            <SideColumn>
              {applyCard && (
                <SectionCard>
                  <ApplyCard
                    kind={applyCard}
                    applyPaused={applyPaused}
                    onApply={
                      profile
                        ? () => openDrawer(ApplyDrawerMode.Apply)
                        : goToSignup
                    }
                    onDecline={() => setDeclineModalOpen(true)}
                    onBrowse={goToProjects}
                    onExplore={profile ? undefined : goToExploreSignup}
                  />
                </SectionCard>
              )}

              {!profile && (
                <SignInHint>
                  Already have an account?{' '}
                  <SignInLink
                    type="button"
                    onClick={goToLogin}
                    data-testid="project-login"
                  >
                    Sign in
                  </SignInLink>
                </SignInHint>
              )}

              {showMatcherBlock && (
                <SectionCard>
                  <MatcherLabel>
                    Your Lemon.io contact for this project:
                  </MatcherLabel>
                  {project.projectAssigneeName && (
                    <MatcherName>{project.projectAssigneeName}</MatcherName>
                  )}
                  {matcherSlackUrl && (
                    <ContactButton
                      href={adjustUrl(matcherSlackUrl)}
                      target="_blank"
                      rel="noopener"
                      data-testid="project-contact-matcher"
                    >
                      Chat in Slack
                    </ContactButton>
                  )}
                </SectionCard>
              )}
            </SideColumn>
          )}
        </PageGrid>
      </PageColumn>

      {showApplyDrawer && (
        <ApplyForm
          open={drawerOpen}
          mode={drawerMode}
          coversSideColumn={showSideColumn}
          width={drawerWidth}
          project={project}
          onClose={() => setDrawerOpen(false)}
          onSubmitted={handleSubmitted}
        />
      )}

      <DeclineOfferDialog
        open={declineModalOpen}
        loading={declineProjectLoading}
        onClose={() => setDeclineModalOpen(false)}
        onConfirm={handleDeclineProject}
      />
    </PageRoot>
  );
};

export default ProjectViewPage;
